'use client';

import {
  ActionIcon,
  Box,
  Divider,
  Group,
  Stack,
  Table,
  TableTbody,
  TableTd,
  TableTr,
  Text,
  Title,
} from '@mantine/core';
import { IconHeart } from '@tabler/icons-react';
import { favoriteProductsAtom } from '@website/atoms';
import type { CartItem, ProductGet } from '@website/types';
import { useAtom } from 'jotai/react';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import Markdown from 'react-markdown';
import { CartAddButton } from './cart-add-button';

export function ProductDetails({
  product,
  productInCart,
}: {
  product: NonNullable<ProductGet>;
  productInCart: CartItem | undefined;
}) {
  const t = useTranslations();
  const [loading, setLoading] = useState(true);
  const [favoriteProducts, setFavoriteProducts] = useAtom(favoriteProductsAtom);

  function toggleFavoriteProduct() {
    favoriteProducts.includes(product.id)
      ? setFavoriteProducts((prev) => prev.filter((id) => id !== product.id))
      : setFavoriteProducts((prev) => [...prev, product.id]);
  }

  useEffect(() => {
    setLoading(false);
  }, []);

  return (
    <Stack px="md" mt="lg">
      <Group justify="space-between">
        <Title order={3}>{product.title}</Title>

        <ActionIcon
          loading={loading}
          onClick={toggleFavoriteProduct}
          variant={favoriteProducts.includes(product.id) ? 'filled' : 'default'}
        >
          <IconHeart />
        </ActionIcon>
      </Group>

      <div style={{ display: 'none' }}>
        {product.comparePrice && (
          <Text c="red" fz={16} td="line-through">
            {product.comparePrice.toLocaleString()}
          </Text>
        )}

        <Text
          fz={20}
          fw={700}
        >{`${product.price.toLocaleString()} ${t('iqd')}`}</Text>
      </div>

      <CartAddButton product={product} productInCart={productInCart} />

      <Divider />

      <Box>
        <Markdown>{product.description}</Markdown>
      </Box>

      <Title order={3}>{t('products.specifications')}</Title>

      <Table withColumnBorders withRowBorders withTableBorder bg="white">
        <TableTbody>
          {product.attributes.map((attribute) => (
            <TableTr key={attribute.id}>
              <TableTd>{attribute.attribute.name}</TableTd>
              <TableTd>{attribute.value}</TableTd>
            </TableTr>
          ))}
        </TableTbody>
      </Table>

      <Box h={32} />
    </Stack>
  );
}
